export { buttonClasses } from "@mui/material";

import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  ButtonTypeMap,
  styled,
} from "@mui/material";
import type { ExtendButtonBase } from "@mui/material/ButtonBase";
import React from "react";
import { withDisplayName } from "../../utils/displayName";

// Add "link" variant and "neutral" color to Button options
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    link: true;
  }

  interface ButtonPropsSizeOverrides {
    inherit: true;
  }

  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

export const ButtonSizes = ["inherit", "small", "medium", "large"] as const;
export type ButtonSize = (typeof ButtonSizes)[number];

export const ButtonVariants = [
  "contained",
  "outlined",
  "text",
  "link",
] as const;
export type ButtonVariant = (typeof ButtonVariants)[number];

export const ButtonColors = [
  "inherit",
  "primary",
  "secondary",
  "success",
  "error",
  "info",
  "warning",
  "neutral",
] as const;
export type ButtonColor = (typeof ButtonColors)[number];

export const ButtonEdges = ["start", "end", false] as const;
export type ButtonEdge = (typeof ButtonEdges)[number];

type LegacyButtonCompatibilityProps = {
  /** Forwarded to the root component, e.g. a router link passed as `component`. */
  to?: unknown;
  /** Forwarded unchanged to the root element. */
  flex?: boolean;
  width?: string | number;
};

type BackpackButtonProps<P extends object = object> = P & {
  edge?: ButtonEdge;
};

export type ButtonProps<
  C extends React.ElementType = ButtonTypeMap["defaultComponent"],
> = MuiButtonProps<C, BackpackButtonProps> & LegacyButtonCompatibilityProps;

const StyledButton = styled<ButtonProps>(MuiButton, {
  shouldForwardProp: (prop: string) => prop !== "edge",
  name: "MuiButton",
})({}) as typeof MuiButton;

const _Button = <C extends React.ElementType = "button">(
  // Specify default props governed by Button theme "variants".
  // Custom props don't play well with variants.
  {
    variant = "outlined",
    color = "primary",
    size = "medium",
    edge = false,
    ...props
  }: ButtonProps<C>,
  ref: React.Ref<HTMLButtonElement>,
) => {
  return (
    <StyledButton
      ref={ref}
      variant={variant}
      color={color}
      size={size}
      edge={edge}
      {...props}
    />
  );
};

type DefaultButtonCompatibilityProps<P extends object> = Omit<
  MuiButtonProps<"button", BackpackButtonProps<P>>,
  "component"
> &
  LegacyButtonCompatibilityProps & {
    component?: never;
  };

export type ExtendableButton<P extends object = object> = ExtendButtonBase<
  ButtonTypeMap<BackpackButtonProps<P>>
> &
  ((props: DefaultButtonCompatibilityProps<P>) => React.JSX.Element);

export const Button = React.forwardRef(_Button) as ExtendableButton<object>;
withDisplayName(Button, "Button");
